/**
 * Fábrica de Anúncios — cria um anúncio NOVO no Mercado Livre pelo método UP Digital, dentro do BI.
 *
 * Etapas: Produto → Pesquisa (ranking automático + links de campeões ML/Shopee, v2 25/09/2026) → Proposta (Claude) → Preço (MC%) → Fotos (OpenAI/Gemini)
 * → Aprovação por bloco → Publicar PAUSADO. Tudo roda no serviço `services/fabrica-anuncios`
 * (porta 8104); esta tela só mostra, deixa editar e aprova. Nada vai para o ML sem o botão
 * "Publicar pausado" e a confirmação escrita — e o anúncio nasce pausado.
 *
 * Referências por marca (pedido de 24/09/2026): fotos e textos-modelo dos anúncios da Mariana,
 * por marca, que entram como referência de estilo nas fotos e no prompt da proposta.
 *
 * Fora do SourceScopeGuard: não lê fonte do BI, só o serviço da Fábrica.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  LinearProgress,
  Link,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Step,
  StepButton,
  Stepper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DomainPageShell } from '../components/DomainPageShell'
import {
  BLOCOS,
  BLOCO_LABEL,
  CONTAS,
  ETAPAS,
  LOJAS_SHOPEE,
  MAX_CONCORRENTES,
  MC_MINIMA_PCT,
  SHOPEE_MARKUP_CHEIO,
  SHOPEE_MC_ALVO_PCT,
  blocosAprovados,
  checarProduto,
  checarTitulo,
  checarTituloCatalogo,
  descricaoFinal,
  etapaSugerida,
  fmtData,
  fotosAprovadas,
  normalizarMlb,
  parseConcorrentes,
  pendenciasPublicacao,
  pendenciasShopee,
  podePublicar,
  podePublicarShopee,
  precoFinal,
  precoShopeeAprovado,
  precificarShopee,
  temCanalShopee,
  produtoVazio,
  segmentarAConfirmar,
  sugerirPrecos,
  tamanhoTitulo,
  temAConfirmar,
  tituloFinal,
  vendidosMedidos,
  type Bloco,
  type Checagem,
  type ContaMl,
  type LojaShopee,
  type ModoPesquisa,
  type Produto,
  type Projeto,
  type Referencia,
  type StatusServico,
} from './fabricaAnuncios/logic'
import * as svc from './fabricaAnuncios/fabricaService'

const brl = (v: number | null | undefined) => (v === null || v === undefined ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }))
const pct = (v: number | null | undefined) => (v === null || v === undefined ? '—' : `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`)
const msg = (e: unknown) => (e instanceof Error ? e.message : String(e))

function Checagens({ itens }: { itens: Checagem[] }) {
  if (!itens.length) return null
  return (
    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
      {itens.map((c) => (
        <Typography key={c.texto} variant="caption" sx={{ color: c.nivel === 'erro' ? 'error.main' : 'warning.main', display: 'block' }}>
          {c.nivel === 'erro' ? '✕ ' : '! '}
          {c.texto}
        </Typography>
      ))}
    </Stack>
  )
}

/** Texto com os `[A CONFIRMAR …]` pintados de amarelo. */
function TextoMarcado({ texto }: { texto: string }) {
  return (
    <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0 }}>
      {segmentarAConfirmar(texto).map((s, i) =>
        s.marcado ? (
          <Box key={i} component="mark" sx={{ bgcolor: '#fff3a0', px: 0.25, borderRadius: 0.5 }}>
            {s.texto}
          </Box>
        ) : (
          <span key={i}>{s.texto}</span>
        ),
      )}
    </Typography>
  )
}

function StatusChip({ s }: { s: string | undefined }) {
  const cor = s === 'ok' || s === 'aprovado' || s === 'pausado' ? 'success' : s === 'rodando' ? 'info' : s === 'erro' ? 'error' : s === 'aguardando_links' ? 'warning' : s === 'editado' ? 'primary' : 'default'
  const rotulo = s === 'aguardando_links' ? 'aguardando links' : s || 'nenhuma'
  return <Chip size="small" color={cor} label={rotulo} />
}

// ================================================================ referências por marca

function PainelReferencias({ marcaAtual, conta }: { marcaAtual: string; conta: ContaMl }) {
  const [aberto, setAberto] = useState(false)
  const [itens, setItens] = useState<Referencia[]>([])
  const [marcas, setMarcas] = useState<Array<{ marca: string; fotos: number; textos: number }>>([])
  const [marca, setMarca] = useState(marcaAtual)
  const [nome, setNome] = useState('')
  const [nota, setNota] = useState('')
  const [texto, setTexto] = useState('')
  const [mlb, setMlb] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const arquivoRef = useRef<HTMLInputElement>(null)

  useEffect(() => setMarca(marcaAtual), [marcaAtual])

  const carregar = useCallback(async () => {
    try {
      const r = await svc.fetchReferencias()
      setItens(r.itens)
      setMarcas(r.marcas)
      setErro(null)
    } catch (e) {
      setErro(msg(e))
    }
  }, [])
  useEffect(() => {
    if (aberto) void carregar()
  }, [aberto, carregar])

  const enviar = async (tipo: 'foto' | 'texto') => {
    setOcupado(true)
    setErro(null)
    try {
      if (tipo === 'foto') {
        const fs = Array.from(arquivoRef.current?.files || [])
        if (!fs.length) throw new Error('Escolha um arquivo de imagem.')
        for (const f of fs) await svc.enviarReferencia({ marca, tipo, nome: nome || f.name, nota, arquivo: f })
        if (arquivoRef.current) arquivoRef.current.value = ''
      } else {
        await svc.enviarReferencia({ marca, tipo, nome, nota, texto })
        setTexto('')
      }
      setNome('')
      await carregar()
    } catch (e) {
      setErro(msg(e))
    } finally {
      setOcupado(false)
    }
  }
  const importar = async () => {
    const id = normalizarMlb(mlb)
    if (!id) return setErro('Informe um MLB válido (ex.: MLB1234567890 ou o link do anúncio).')
    setOcupado(true)
    try {
      await svc.importarAnuncioReferencia({ marca, mlb: id, conta, nota })
      setMlb('')
      await carregar()
    } catch (e) {
      setErro(msg(e))
    } finally {
      setOcupado(false)
    }
  }
  const remover = async (id: string) => {
    try {
      await svc.removerReferencia(id)
      await carregar()
    } catch (e) {
      setErro(msg(e))
    }
  }

  const daMarca = itens.filter((i) => !marca.trim() || i.marca.trim().toLowerCase() === marca.trim().toLowerCase())

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Referências por marca
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Seus anúncios-modelo: fotos e textos que a Fábrica usa como padrão da marca.{' '}
              {marcas.length ? marcas.map((m) => `${m.marca} (${m.fotos} fotos, ${m.textos} textos)`).join(' · ') : ''}
            </Typography>
          </Box>
          <Button size="small" onClick={() => setAberto((v) => !v)}>
            {aberto ? 'Fechar' : 'Gerenciar'}
          </Button>
        </Stack>
        <Collapse in={aberto}>
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            {erro && <Alert severity="error">{erro}</Alert>}
            <Alert severity="info" sx={{ py: 0 }}>
              <b>Identidade Fiesta</b> é o banco geral: fotos e textos salvos nessa "marca" entram como referência em <b>toda</b> geração, seja qual for a marca do projeto. As demais marcas só entram nos projetos delas.
            </Alert>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <Stack direction="row" spacing={0.5}>
                <TextField size="small" label="Marca" value={marca} onChange={(e) => setMarca(e.target.value)} sx={{ minWidth: 220 }} />
                <Button size="small" variant={marca === 'Identidade Fiesta' ? 'contained' : 'outlined'} onClick={() => setMarca('Identidade Fiesta')} sx={{ whiteSpace: 'nowrap' }}>
                  Identidade
                </Button>
              </Stack>
              <TextField size="small" label="Nome da referência" value={nome} onChange={(e) => setNome(e.target.value)} fullWidth />
              <TextField size="small" label="Nota (o que copiar dela)" value={nota} onChange={(e) => setNota(e.target.value)} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center">
              <Button component="label" size="small" variant="outlined" disabled={ocupado || !marca} title={marca ? 'Escolha as fotos: o envio começa na hora' : 'Preencha a marca primeiro'}>
                {ocupado ? 'Enviando…' : 'Escolher e subir foto(s)'}
                <input ref={arquivoRef} type="file" accept="image/*,.heic,.heif" multiple hidden onChange={() => void enviar('foto')} />
              </Button>
              <Divider flexItem orientation="vertical" />
              <TextField size="small" label="MLB ou link de anúncio seu" value={mlb} onChange={(e) => setMlb(e.target.value)} sx={{ minWidth: 260 }} />
              <Button size="small" variant="outlined" disabled={ocupado || !marca} onClick={() => void importar()}>
                Importar anúncio (título + descrição + 4 fotos)
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <TextField size="small" label="Texto-modelo (título/descrição que você quer como padrão)" value={texto} onChange={(e) => setTexto(e.target.value)} multiline minRows={2} fullWidth />
              <Button size="small" variant="outlined" disabled={ocupado || !marca || !texto.trim()} onClick={() => void enviar('texto')}>
                Salvar texto
              </Button>
            </Stack>
            {ocupado && <LinearProgress />}
            <Grid container spacing={1}>
              {daMarca.map((r) => (
                <Grid key={r.id} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    {r.tipo === 'foto' ? (
                      <Box component="img" src={svc.urlReferencia(r.id)} alt={r.nome} sx={{ width: '100%', aspectRatio: '1', objectFit: 'contain', bgcolor: '#fafafa' }} />
                    ) : r.fotos_url?.[0] ? (
                      <Box component="img" src={r.fotos_url[0]} alt={r.nome} sx={{ width: '100%', aspectRatio: '1', objectFit: 'contain', bgcolor: '#fafafa' }} />
                    ) : (
                      <Box sx={{ p: 1, height: 96, overflow: 'hidden', fontSize: 11, color: 'text.secondary', whiteSpace: 'pre-wrap' }}>{r.texto.slice(0, 220)}</Box>
                    )}
                    <Box sx={{ p: 0.75 }}>
                      <Typography variant="caption" fontWeight={600} display="block" noWrap title={r.nome}>
                        {r.nome}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {r.marca} · {r.tipo}
                        {r.origem && r.origem !== 'upload' ? ` · ${r.origem}` : ''}
                      </Typography>
                      <Button size="small" color="error" onClick={() => void remover(r.id)}>
                        remover
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
              {!daMarca.length && (
                <Grid size={12}>
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma referência para {marca || 'esta marca'} ainda.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  )
}

// ================================================================ etapa 0 — produto

function EtapaProduto({ p, onSalvo }: { p: Projeto | null; onSalvo: (p: Projeto) => void }) {
  const [f, setF] = useState<Produto>(p?.produto || produtoVazio())
  const [erro, setErro] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const fotosRef = useRef<HTMLInputElement>(null)
  useEffect(() => setF(p?.produto || produtoVazio()), [p?.id, p?.produto])
  const checks = checarProduto(f)
  const set = (k: keyof Produto, v: unknown) => setF((x) => ({ ...x, [k]: v }))
  const setEmb = (k: keyof NonNullable<Produto['embalagem']>, v: string) => setF((x) => ({ ...x, embalagem: { ...(x.embalagem || {}), [k]: v === '' ? undefined : Number(v) } }))

  const salvar = async () => {
    setOcupado(true)
    setErro(null)
    try {
      const r = p ? await svc.editarProjeto(p.id, { produto: f }) : await svc.criarProjeto(f)
      onSalvo(r.projeto)
    } catch (e) {
      setErro(msg(e))
    } finally {
      setOcupado(false)
    }
  }
  const buscarCmv = async () => {
    try {
      const r = await svc.fetchCmv(f.sku ? { sku: f.sku } : { termo: f.termo_busca || f.nome })
      if (r.encontrado) {
        set('cmv', r.cmv ?? null)
        set('impostos_pct', r.impostos_pct ?? null)
        if (!f.sku && r.sku) set('sku', r.sku)
      } else setErro(r.erro || 'CMV não encontrado em margens para esse SKU/termo (dado ausente, não zero).')
    } catch (e) {
      setErro(msg(e))
    }
  }
  const [avisoFotos, setAvisoFotos] = useState<string | null>(null)
  /** Envia assim que a Mariana escolhe os arquivos — antes eram dois cliques (escolher + enviar)
   *  e o segundo ficava desabilitado até salvar o produto, sem dizer por quê (relato de 24/09). */
  const enviarFotos = async (lista: FileList | null) => {
    const fs = Array.from(lista || [])
    if (fotosRef.current) fotosRef.current.value = ''
    if (!fs.length) return
    if (!p) return setErro('Salve o produto (botão "Criar projeto") antes de enviar as fotos reais.')
    setOcupado(true)
    setErro(null)
    setAvisoFotos(null)
    try {
      const r = await svc.enviarFotosReais(p.id, fs)
      onSalvo(r.projeto)
      if (r.recusados?.length) setAvisoFotos(`Não entraram: ${r.recusados.map((x) => `${x.nome} (${x.motivo})`).join('; ')}`)
    } catch (e) {
      setErro(msg(e))
    } finally {
      setOcupado(false)
    }
  }

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Só o que você confirmar aqui é tratado como <b>dado real</b>. O resto a IA marca como <mark>[A CONFIRMAR]</mark> e a publicação fica travada até resolver.
      </Alert>
      {erro && <Alert severity="error">{erro}</Alert>}
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField label="Nome do produto" value={f.nome} onChange={(e) => set('nome', e.target.value)} fullWidth required />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField label="Marca (BRAND)" value={f.marca} onChange={(e) => set('marca', e.target.value)} fullWidth required />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Conta</InputLabel>
            <Select label="Conta" value={f.conta} onChange={(e) => set('conta', e.target.value as ContaMl)}>
              {CONTAS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField label="Termo de busca (como o cliente procura)" value={f.termo_busca} onChange={(e) => set('termo_busca', e.target.value)} fullWidth placeholder="ex.: arvore de natal 1,80m" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body2" color="text.secondary">
              Canais:
            </Typography>
            <FormControlLabel control={<Checkbox size="small" checked disabled />} label="Mercado Livre" />
            <FormControlLabel
              control={<Checkbox size="small" checked={!!f.canais?.includes('shopee')} onChange={(e) => set('canais', e.target.checked ? ['ml', 'shopee'] : ['ml'])} />}
              label="Shopee"
            />
            {f.canais?.includes('shopee') && (
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel>Loja Shopee</InputLabel>
                <Select label="Loja Shopee" value={f.loja_shopee || '1'} onChange={(e) => set('loja_shopee', e.target.value as LojaShopee)}>
                  {(Object.keys(LOJAS_SHOPEE) as LojaShopee[]).map((k) => (
                    <MenuItem key={k} value={k}>
                      {LOJAS_SHOPEE[k]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <TextField label="SKU" value={f.sku || ''} onChange={(e) => set('sku', e.target.value)} fullWidth />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <TextField label="EAN / GTIN" value={f.ean || ''} onChange={(e) => set('ean', e.target.value)} fullWidth helperText="vazio = EMPTY_GTIN_REASON" />
        </Grid>
        {(['altura', 'largura', 'comprimento'] as const).map((k) => (
          <Grid key={k} size={{ xs: 4, md: 2 }}>
            <TextField label={`Emb. ${k} (cm)`} type="number" value={f.embalagem?.[k] ?? ''} onChange={(e) => setEmb(k, e.target.value)} fullWidth />
          </Grid>
        ))}
        <Grid size={{ xs: 4, md: 2 }}>
          <TextField label="Emb. peso (g)" type="number" value={f.embalagem?.peso_g ?? ''} onChange={(e) => setEmb('peso_g', e.target.value)} fullWidth />
        </Grid>
        <Grid size={{ xs: 4, md: 2 }}>
          <TextField label="Estoque" type="number" value={f.estoque ?? ''} onChange={(e) => set('estoque', e.target.value === '' ? null : Number(e.target.value))} fullWidth />
        </Grid>
        <Grid size={{ xs: 4, md: 2 }}>
          <TextField label="CMV (R$)" type="number" value={f.cmv ?? ''} onChange={(e) => set('cmv', e.target.value === '' ? null : Number(e.target.value))} fullWidth />
        </Grid>
        <Grid size={{ xs: 6, md: 2 }}>
          <TextField label="Impostos (%)" type="number" value={f.impostos_pct ?? ''} onChange={(e) => set('impostos_pct', e.target.value === '' ? null : Number(e.target.value))} fullWidth />
        </Grid>
        <Grid size={{ xs: 6, md: 4 }}>
          <Button variant="outlined" onClick={() => void buscarCmv()} sx={{ height: '100%' }} fullWidth>
            Buscar CMV e impostos em margens
          </Button>
        </Grid>
        <Grid size={12}>
          <TextField label="O que está confirmado do produto (medidas reais, material, conteúdo da caixa, o que NÃO tem)" value={f.confirmado || ''} onChange={(e) => set('confirmado', e.target.value)} fullWidth multiline minRows={3} helperText="É daqui que a IA tira a ficha e a descrição. O que não estiver aqui nem no catálogo vira [A CONFIRMAR]." />
        </Grid>
      </Grid>
      <Checagens itens={checks} />
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button variant="contained" disabled={ocupado || checks.some((c) => c.nivel === 'erro')} onClick={() => void salvar()}>
          {p ? 'Salvar produto' : 'Criar projeto'}
        </Button>
        <Divider flexItem orientation="vertical" />
        <Button component="label" variant="outlined" disabled={ocupado} title={p ? 'Escolha as fotos: o envio começa na hora' : 'Crie o projeto primeiro'}>
          {ocupado ? 'Enviando…' : 'Escolher e enviar fotos reais'}
          <input ref={fotosRef} type="file" accept="image/*,.heic,.heif" multiple hidden onChange={(e) => void enviarFotos(e.target.files)} />
        </Button>
        <Typography variant="caption" color={p ? 'text.secondary' : 'warning.main'}>
          {p ? 'A foto do produto inteiro é a referência principal das 9 fotos. JPG, PNG, WEBP ou HEIC do celular.' : 'Crie o projeto (botão ao lado) para liberar o envio das fotos.'}
        </Typography>
      </Stack>
      {avisoFotos && <Alert severity="warning" onClose={() => setAvisoFotos(null)}>{avisoFotos}</Alert>}
      {p && p.fotos_reais.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {p.fotos_reais.map((fr) => (
            <Box key={fr.arquivo} component="img" src={svc.urlFotoReal(p.id, fr.arquivo)} alt={fr.nome} title={fr.nome} sx={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
          ))}
        </Stack>
      )}
    </Stack>
  )
}

// ================================================================ etapa 1 — pesquisa

function Prova({ mlbs }: { mlbs?: string[] }) {
  if (!mlbs?.length) return <Chip size="small" color="warning" label="sem prova" sx={{ ml: 0.5 }} />
  return (
    <Tooltip title={mlbs.join(', ')}>
      <Chip size="small" variant="outlined" label={`${mlbs.length} campeõ${mlbs.length === 1 ? 'o' : 'es'}`} sx={{ ml: 0.5 }} />
    </Tooltip>
  )
}

function ListaSimples({ itens }: { itens?: string[] }) {
  if (!itens?.length) return <Typography variant="caption" color="text.secondary">—</Typography>
  return (
    <List dense disablePadding>
      {itens.map((t, i) => (
        <ListItemText key={i} primary={<Typography variant="body2">• {t}</Typography>} />
      ))}
    </List>
  )
}

/** Pesquisa v2 (25/09/2026): ranking automático como primeira tentativa; links de campeões (ML e Shopee)
 *  colados pela Mariana — complementares ou sozinhos ("o operador escolhe"); coleta por campeão, análise
 *  cruzada com prova por item, posicionamento de preço, catálogo/Buy Box, comparação com os próprios e
 *  relatório salvo. Tudo entra na proposta. */
function EtapaPesquisa({ p, status, disparar, salvarLinks }: { p: Projeto; status: StatusServico | null; disparar: (soCampeoes: boolean) => Promise<void>; salvarLinks: (texto: string, modo: ModoPesquisa) => Promise<void> }) {
  const q = p.pesquisa
  const rk = q.ranking
  const [texto, setTexto] = useState((p.produto.concorrentes || []).join('\n'))
  const [modo, setModo] = useState<ModoPesquisa>(p.produto.modo_pesquisa || 'auto')
  const [aba, setAba] = useState(0)
  useEffect(() => {
    setTexto((p.produto.concorrentes || []).join('\n'))
    setModo(p.produto.modo_pesquisa || 'auto')
  }, [p.id, p.produto.concorrentes, p.produto.modo_pesquisa])
  const parse = useMemo(() => parseConcorrentes(texto), [texto])
  const validos = parse.filter((c) => !c.erro).length
  const sujo = texto.trim() !== (p.produto.concorrentes || []).join('\n').trim() || modo !== (p.produto.modo_pesquisa || 'auto')
  const aguardando = q.status === 'aguardando_links'
  const an = q.analise
  const pos = q.posicionamento
  const medidos = vendidosMedidos(q.campeoes)
  const ultimo = status?.scraping?.ultimo

  const pesquisar = async (soCampeoes: boolean) => {
    if (sujo) await salvarLinks(texto, modo)
    await disparar(soCampeoes)
  }

  return (
    <Stack spacing={2}>
      <Alert severity="info" icon={false}>
        <b>Como funciona:</b> o ranking automático (Playwright na lista do ML) é a primeira tentativa. Se ele falhar, ou quando você quiser, cole de 1 a {MAX_CONCORRENTES} links de anúncios campeões — <b>Mercado Livre ou Shopee</b>. Os links vêm primeiro; o ranking completa. Cada campeão é coletado (API do ML; página pública na Shopee), a IA cruza tudo com prova por item, e <b>tudo entra na proposta</b>.
        {ultimo && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            Ranking automático, última execução {ultimo.quando ? fmtData(ultimo.quando) : ''}: {ultimo.ok ? `OK (${ultimo.fonte})` : `falhou (${ultimo.motivo})`}.
          </Typography>
        )}
      </Alert>

      {/* ---- links + modo */}
      <Card variant="outlined" sx={{ p: 1.5, bgcolor: aguardando ? '#fff8e1' : undefined }}>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2">Anúncios concorrentes (campeões)</Typography>
            <RadioGroup row value={modo} onChange={(e) => setModo(e.target.value as ModoPesquisa)}>
              <FormControlLabel value="auto" control={<Radio size="small" />} label="Ranking automático + meus links" />
              <FormControlLabel value="links" control={<Radio size="small" />} label="Só os meus links" />
            </RadioGroup>
          </Stack>
          {aguardando && (
            <Alert severity="warning">
              {q.motivo_links || 'O ranking automático não respondeu. Cole os links dos campeões e pesquise com eles.'}
            </Alert>
          )}
          <TextField
            label={`Cole 1 link (ou MLB) por linha — até ${MAX_CONCORRENTES}`}
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            multiline
            minRows={3}
            fullWidth
            placeholder={'https://produto.mercadolivre.com.br/MLB-...\nhttps://www.mercadolivre.com.br/.../p/MLB...\nhttps://shopee.com.br/...-i.SHOP.ITEM'}
          />
          {!!parse.length && (
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
              {parse.map((c, i) => (
                <Tooltip key={i} title={c.erro || c.entrada}>
                  <Chip
                    size="small"
                    color={c.erro ? 'error' : c.canal === 'shopee' ? 'warning' : 'primary'}
                    variant="outlined"
                    label={c.erro ? `✕ ${c.entrada.slice(0, 28)}…` : c.canal === 'shopee' ? `Shopee ${c.shop_id}/${c.item_id}` : c.mlb ? `${c.mlb}${c.catalogo ? ' (catálogo)' : ''}` : c.catalogo ? `catálogo ${c.catalogo}` : 'link encurtado'}
                  />
                </Tooltip>
              ))}
            </Stack>
          )}
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={() => void pesquisar(false)} disabled={q.status === 'rodando' || (modo === 'links' && validos === 0)}>
              {q.status === 'ok' ? 'Pesquisar de novo (tudo)' : 'Pesquisar'}
            </Button>
            {(aguardando || q.status === 'ok') && (
              <Button variant="outlined" onClick={() => void pesquisar(true)} disabled={q.status === 'rodando' || validos === 0}>
                Pesquisar com estes links (só os campeões)
              </Button>
            )}
            <StatusChip s={q.status} />
            {q.status === 'rodando' && <CircularProgress size={18} />}
            <Typography variant="caption" color="text.secondary">
              {validos} link{validos === 1 ? '' : 's'} válido{validos === 1 ? '' : 's'}{sujo ? ' · será salvo ao pesquisar' : ''} · o que a conta já tem · categoria · CMV · ranking · campeões · análise · preço · relatório
            </Typography>
          </Stack>
        </Stack>
      </Card>

      {q.status === 'erro' && <Alert severity="error">{q.erro}</Alert>}
      {(q.status === 'ok' || aguardando) && (
        <>
          {!!q.inconsistencias?.length && (
            <Alert severity="warning">
              <b>Anúncios próprios inconsistentes:</b> {q.inconsistencias.join(' · ')}
            </Alert>
          )}
          {q.catalogo_proprio && (
            <Alert severity="success">
              Existe produto de catálogo da própria marca ({q.catalogo_proprio.catalogo}) no anúncio {q.catalogo_proprio.id}: o novo anúncio será <b>vinculado</b> a ele ao publicar.
            </Alert>
          )}
          {!!q.avisos?.length && <Alert severity="info">{q.avisos.join(' · ')}</Alert>}
          {!!q.erros?.length && (
            <Alert severity="info">
              Passos que falharam (o resto seguiu): {q.erros.join(' · ')}
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2">Categoria</Typography>
              <Typography variant="body2">
                {q.categoria ? `${q.categoria.caminho} (${q.categoria.id}) · título máx. ${q.categoria.max_title_length}` : 'não encontrada'}
              </Typography>
              {q.categoria && (
                <Typography variant="caption" color="text.secondary">
                  Obrigatórios: {q.categoria.obrigatorios.map((a) => a.nome).join(', ') || '—'} · Condicionais: {q.categoria.condicionais.map((a) => a.nome).join(', ') || '—'}
                </Typography>
              )}
              <Typography variant="subtitle2" sx={{ mt: 1.5 }}>
                CMV (financial.margens)
              </Typography>
              <Typography variant="body2">
                {q.cmv?.encontrado ? `${brl(q.cmv.cmv)} · impostos ${pct(q.cmv.impostos_pct)} · SKU ${q.cmv.sku}` : q.cmv?.erro || 'não encontrado — informe em Produto (não é zero, é ausente)'}
              </Typography>
              <Typography variant="subtitle2" sx={{ mt: 1.5 }}>
                Palavras (peso por vendas)
              </Typography>
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
                {(q.palavras || []).slice(0, 20).map((w) => (
                  <Chip key={w.palavra} size="small" label={`${w.palavra} ${w.peso}%`} />
                ))}
              </Stack>
              {!!q.nao_usar?.length && (
                <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                  Não usar (terceiros): {q.nao_usar.join(', ')}
                </Typography>
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2">A conta já tem ({q.conta_propria?.length || 0})</Typography>
              <List dense disablePadding>
                {(q.conta_propria || []).slice(0, 8).map((it) => (
                  <ListItemText
                    key={it.id}
                    primary={
                      <Typography variant="body2">
                        <Link href={it.permalink || undefined} target="_blank" rel="noreferrer">
                          {it.id}
                        </Link>{' '}
                        {it.titulo}
                      </Typography>
                    }
                    secondary={`${it.status} · ${it.vendidos ?? 0} vend. · marca ${it.marca || '—'} · SKU ${it.sku || '—'} · catálogo ${it.catalogo || '—'}`}
                  />
                ))}
              </List>
            </Grid>
          </Grid>
          {q.shopee && (
            <Card variant="outlined" sx={{ p: 1.5, bgcolor: '#fff7f2' }}>
              <Typography variant="subtitle2">Shopee — {q.shopee.loja_nome}</Typography>
              <Typography variant="body2">
                Loja: {q.shopee.shop?.shop_name || '—'} ({q.shopee.shop?.status || 'sem resposta'}) · categoria sugerida: {q.shopee.categoria_id ?? 'não encontrada'}
                {q.shopee.categorias && q.shopee.categorias.length > 1 ? ` (outras: ${q.shopee.categorias.slice(1).join(', ')})` : ''}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Canais de envio: {(q.shopee.canais || []).map((c) => c.nome).join(', ') || '—'} · Marca na Shopee: {(q.shopee.marcas || []).map((m) => m.nome).join(', ') || 'não encontrada (vai como NoBrand)'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Atributos obrigatórios: {(q.shopee.atributos || []).filter((a) => a.obrigatorio).map((a) => a.nome).join(', ') || '—'}
              </Typography>
              {!!q.shopee.erros?.length && (
                <Typography variant="caption" color="error.main" display="block">
                  Falhas: {q.shopee.erros.join(' · ')}
                </Typography>
              )}
            </Card>
          )}

          {/* ---- ranking automático */}
          <Typography variant="subtitle2">Ranking automático ({rk?.fonte || '—'})</Typography>
          {rk?.indisponivel ? (
            <Alert severity={rk.pulado ? 'info' : 'warning'}>
              {rk.pulado ? 'Ranking automático pulado (modo "só os meus links").' : `Ranking indisponível: ${rk.motivo}. Os campeões vêm dos links colados acima. Se o Chromium pediu login/CAPTCHA, exporte os cookies do seu Chrome (Cookie-Editor → Export JSON) e mande ao Ronaldo para a VPS. A Fábrica não inventa ranking.`}
            </Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Título</TableCell>
                  <TableCell>Vendedor</TableCell>
                  <TableCell align="right">Vendidos</TableCell>
                  <TableCell align="right">Preço</TableCell>
                  <TableCell>Catálogo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rk?.itens || []).slice(0, 10).map((i, idx) => (
                  <TableRow key={`${i.mlb}-${idx}`}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Link href={i.permalink || undefined} target="_blank" rel="noreferrer">
                        {i.titulo}
                      </Link>
                    </TableCell>
                    <TableCell>{i.vendedor || '—'}</TableCell>
                    <TableCell align="right">{i.vendidos?.toLocaleString('pt-BR')}</TableCell>
                    <TableCell align="right">{brl(i.preco)}</TableCell>
                    <TableCell>{i.catalogo || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* ---- campeões + posicionamento de preço */}
          {q.status === 'ok' && (
            <Card variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1" fontWeight={600}>
                  Campeões analisados ({q.campeoes?.length || 0}) · posicionamento de preço
                </Typography>
                <Chip size="small" label={`vendidos medidos em ${medidos.medidos} de ${medidos.total}`} color={medidos.medidos ? 'default' : 'warning'} />
                {/* o ML fechou o "+X vendidos" de terceiro na página; visitas/30d é o sinal de procura que a
                    API oficial ainda entrega, por isso aparece ao lado e não escondido só no prompt da IA */}
                <Chip size="small" label={`visitas (30d) em ${(pos?.por_campeao || []).filter((l) => l.visitas_30d != null).length} de ${medidos.total}`} />
                <Link href={svc.urlRelatorioPesquisa(p.id)} target="_blank" rel="noreferrer">
                  abrir relatório de pesquisa
                </Link>
              </Stack>
              {pos?.n ? (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Mercado: mínimo {brl(pos.min)} · mediana <b>{brl(pos.mediana)}</b> · máximo {brl(pos.max)}
                  {pos.media_ponderada_por_vendidos ? ` · média ponderada por vendidos ${brl(pos.media_ponderada_por_vendidos)} (${pos.vendidos_medidos_em} medidos)` : ''}
                  {pos.posicao_do_proprio ? ` · seu preço de referência: ${pos.posicao_do_proprio}` : ''}
                </Typography>
              ) : (
                <Typography variant="caption" color="text.secondary">nenhum campeão com preço coletado</Typography>
              )}
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Canal</TableCell>
                    <TableCell>Anúncio</TableCell>
                    <TableCell align="right">Preço</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Full</TableCell>
                    <TableCell align="right">Vendidos</TableCell>
                    <TableCell align="right">Visitas (30d)</TableCell>
                    <TableCell>Reputação</TableCell>
                    <TableCell>Catálogo</TableCell>
                    <TableCell align="right">★</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(pos?.por_campeao || []).map((l) => (
                    <TableRow key={`${l.canal}-${l.id}`}>
                      <TableCell>{l.canal === 'shopee' ? 'Shopee' : 'ML'}</TableCell>
                      <TableCell>
                        <Link href={l.permalink || undefined} target="_blank" rel="noreferrer">
                          {l.titulo || l.id}
                        </Link>
                      </TableCell>
                      <TableCell align="right">
                        {brl(l.preco)}
                        {l.preco_original && l.preco_original > l.preco ? <Typography variant="caption" color="text.secondary" display="block">de {brl(l.preco_original)}</Typography> : null}
                      </TableCell>
                      <TableCell>{l.tipo === 'gold_pro' ? 'premium' : l.tipo === 'gold_special' ? 'clássico' : l.tipo || '—'}</TableCell>
                      <TableCell>{l.full ? 'sim' : ''}</TableCell>
                      <TableCell align="right">{l.vendidos != null ? `${l.vendidos.toLocaleString('pt-BR')} (${l.vendidos_fonte})` : <Chip size="small" label="não medido" />}</TableCell>
                      <TableCell align="right">
                        {l.visitas_30d != null ? l.visitas_30d.toLocaleString('pt-BR') : '—'}
                        {l.visitas_total != null ? <Typography variant="caption" color="text.secondary" display="block">{l.visitas_total.toLocaleString('pt-BR')} no total</Typography> : null}
                      </TableCell>
                      <TableCell>{l.reputacao ?? '—'}{l.loja_oficial ? ' · oficial' : ''}</TableCell>
                      <TableCell>{l.catalogo || '—'}</TableCell>
                      <TableCell align="right">{l.media_avaliacoes ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {(q.campeoes || []).some((c) => c.erros?.length) && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Falhas por campeão: {(q.campeoes || []).filter((c) => c.erros?.length).map((c) => `${c.mlb || c.item_id}: ${c.erros.join('; ')}`).join(' · ')}
                </Typography>
              )}
              {!!q.catalogo_campeoes?.buy_box?.length && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <b>Catálogo / Buy Box:</b> {q.catalogo_campeoes.em_catalogo} campeão(ões) em catálogo.{' '}
                  {q.catalogo_campeoes.buy_box.map((b) => `${b.catalogo}: vence ${b.vencedor || '—'} a ${brl(b.preco)}${b.concorrentes_no_catalogo != null ? ` entre ${b.concorrentes_no_catalogo} vendedores` : ''}${b.preco_min_catalogo ? ` (${brl(b.preco_min_catalogo)}–${brl(b.preco_max_catalogo)})` : ''}`).join(' · ')}
                </Typography>
              )}
            </Card>
          )}

          {/* ---- comparação com os próprios */}
          {q.status === 'ok' && q.comparacao?.tem_proprios && (
            <Card variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Comparação com os meus anúncios
              </Typography>
              {q.comparacao.fotos_por_campeao_media != null && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Campeões têm em média {q.comparacao.fotos_por_campeao_media} fotos.
                </Typography>
              )}
              <List dense disablePadding>
                {(q.comparacao.proprios || []).map((m) => (
                  <ListItemText
                    key={m.id}
                    primary={
                      <Typography variant="body2">
                        <b>{m.id}</b> {m.titulo} · {brl(m.preco)}
                        {m.preco_vs_mediana_pct != null ? ` (${m.preco_vs_mediana_pct > 0 ? '+' : ''}${m.preco_vs_mediana_pct}% vs mediana)` : ''}
                      </Typography>
                    }
                    secondary={m.atributos_que_campeoes_preenchem_e_eu_nao.length ? `Atributos que os campeões preenchem e este não: ${m.atributos_que_campeoes_preenchem_e_eu_nao.join(', ')}` : 'ficha cobre o que os campeões preenchem'}
                  />
                ))}
              </List>
              {!!an?.diferenciais_proprios?.length && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    O que só você declara (diferenciais)
                  </Typography>
                  <ListaSimples itens={an.diferenciais_proprios} />
                </>
              )}
            </Card>
          )}

          {/* ---- análise dos campeões (IA, com prova) */}
          {q.status === 'ok' && (
            <Card variant="outlined" sx={{ p: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle1" fontWeight={600}>
                  Análise dos campeões {an?.modelo ? `(${an.modelo})` : ''}
                </Typography>
                {an?.com_grade ? <Chip size="small" label="com grade visual" /> : <Chip size="small" variant="outlined" label="sem grade visual" />}
                {!!an?.sem_prova?.length && (
                  <Tooltip title={an.sem_prova.join(' · ')}>
                    <Chip size="small" color="warning" label={`${an.sem_prova.length} item(ns) sem prova`} />
                  </Tooltip>
                )}
              </Stack>
              {an?.erro && <Alert severity="warning" sx={{ mt: 1 }}>{an.erro}</Alert>}
              {an && !an.erro && (
                <>
                  {an.resumo && (
                    <Alert severity="success" icon={false} sx={{ mt: 1 }}>
                      {an.resumo}
                    </Alert>
                  )}
                  <Tabs value={aba} onChange={(_, v) => setAba(v)} variant="scrollable" scrollButtons="auto" sx={{ mt: 1 }}>
                    <Tab label="Títulos e palavras" />
                    <Tab label="Ficha" />
                    <Tab label="Descrição" />
                    <Tab label="Avaliações" />
                    <Tab label="Perguntas" />
                    <Tab label="Visual" />
                    <Tab label="Objeções" />
                  </Tabs>
                  <Box sx={{ pt: 1.5 }}>
                    {aba === 0 && (
                      <Stack spacing={1}>
                        <Typography variant="body2">
                          <b>Estrutura dominante:</b> {an.titulos?.estrutura_dominante || '—'}
                        </Typography>
                        <Typography variant="subtitle2">Termos comuns</Typography>
                        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
                          {(an.titulos?.termos_comuns || []).map((t, i) => (
                            <Box key={i} sx={{ display: 'inline-flex', alignItems: 'center' }}>
                              <Chip size="small" label={`${t.termo} · em ${t.em}`} />
                              <Prova mlbs={t.mlbs} />
                            </Box>
                          ))}
                        </Stack>
                        <Typography variant="subtitle2">Termos exclusivos (só um usa)</Typography>
                        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
                          {(an.titulos?.termos_exclusivos || []).map((t, i) => (
                            <Chip key={i} size="small" variant="outlined" label={t.termo} />
                          ))}
                        </Stack>
                        {!!an.titulos?.marcas_terceiros?.length && (
                          <Typography variant="body2" color="error.main">
                            Marcas de terceiro (nunca usar): {an.titulos.marcas_terceiros.join(', ')}
                          </Typography>
                        )}
                        <Typography variant="subtitle2">Brecha (ninguém diz)</Typography>
                        <ListaSimples itens={an.titulos?.brecha} />
                      </Stack>
                    )}
                    {aba === 1 && (
                      <Stack spacing={1}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Atributo</TableCell>
                              <TableCell align="right">Campeões preenchem</TableCell>
                              <TableCell>Valores</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(an.ficha?.atributos || []).map((a) => (
                              <TableRow key={a.id}>
                                <TableCell>{a.id}</TableCell>
                                <TableCell align="right">{a.preenchem}</TableCell>
                                <TableCell>{Object.entries(a.valores || {}).map(([v, n]) => `${v} (${n})`).join(' · ')}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                        <Typography variant="subtitle2">Permitidos que ninguém preenche (brecha da ficha)</Typography>
                        <ListaSimples itens={an.ficha?.permitidos_que_ninguem_preenche} />
                        {!!an.ficha?.obrigatorios_faltando_no_produto?.length && (
                          <Typography variant="body2" color="warning.main">
                            Obrigatórios que faltam no seu produto: {an.ficha.obrigatorios_faltando_no_produto.join(', ')}
                          </Typography>
                        )}
                      </Stack>
                    )}
                    {aba === 2 && (
                      <Stack spacing={1}>
                        <Typography variant="body2">
                          <b>Tom:</b> {an.descricao?.tom || '—'} · <b>tamanho médio:</b> {an.descricao?.tamanho_medio_caracteres ?? '—'} caracteres
                        </Typography>
                        <Typography variant="subtitle2">Seções comuns</Typography>
                        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5}>
                          {(an.descricao?.secoes_comuns || []).map((s, i) => (
                            <Chip key={i} size="small" label={`${s.secao} · em ${s.em}`} />
                          ))}
                        </Stack>
                        <Typography variant="subtitle2">O que falta em todas (o seu vai ter)</Typography>
                        <ListaSimples itens={an.descricao?.o_que_falta_em_todas} />
                      </Stack>
                    )}
                    {aba === 3 && (
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" color="error.main">
                            O que decepciona (notas 1–3) → avisos e fotos
                          </Typography>
                          {(an.avaliacoes?.decepciona || []).map((t, i) => (
                            <Box key={i} sx={{ mb: 0.75 }}>
                              <Typography variant="body2">
                                <b>{t.tema}</b> · {t.freq}× <Prova mlbs={t.mlbs} />
                              </Typography>
                              {t.exemplo && <Typography variant="caption" color="text.secondary">“{t.exemplo}”</Typography>}
                            </Box>
                          ))}
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <Typography variant="subtitle2" color="success.main">
                            O que encanta (notas 4–5) → benefícios comprovados
                          </Typography>
                          {(an.avaliacoes?.encanta || []).map((t, i) => (
                            <Box key={i} sx={{ mb: 0.75 }}>
                              <Typography variant="body2">
                                <b>{t.tema}</b> · {t.freq}× <Prova mlbs={t.mlbs} />
                              </Typography>
                              {t.exemplo && <Typography variant="caption" color="text.secondary">“{t.exemplo}”</Typography>}
                            </Box>
                          ))}
                        </Grid>
                        {/* Sem texto de avaliação a aba ficava com dois títulos e nada embaixo. É o caso da
                            Shopee: ela dá a nota e a distribuição por estrela, mas não libera o texto. */}
                        {!(an.avaliacoes?.decepciona || []).length && !(an.avaliacoes?.encanta || []).length && (
                          <Grid size={12}>
                            {(q.avaliacoes || []).map((a) => (
                              <Typography key={a.mlb} variant="body2">
                                {a.mlb} · nota <b>{a.media ?? '—'}</b> em {a.total ?? 0} avaliações
                                {a.por_estrela ? ` · ${Object.entries(a.por_estrela).reverse().map(([e, n]) => `★${e}: ${n}`).join(' · ')}` : ''}
                              </Typography>
                            ))}
                            <Typography variant="caption" color="warning.main">
                              {(q.avaliacoes || []).find((a) => a.sem_textos_motivo)?.sem_textos_motivo
                                || 'Nenhum campeão tem avaliação com texto — a IA usa as objeções prováveis do nicho na FAQ.'}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    )}
                    {aba === 4 && (
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">FAQ obrigatória (pergunta repetida entre campeões)</Typography>
                        {(an.perguntas?.faq_obrigatoria || []).map((f, i) => (
                          <Box key={i}>
                            <Typography variant="body2">
                              <b>{f.pergunta}</b> · em {f.em_quantos_anuncios} anúncio(s) <Prova mlbs={f.mlbs} />
                            </Typography>
                            {f.resposta_padrao_dos_campeoes && <Typography variant="caption" color="text.secondary">Resposta padrão: {f.resposta_padrao_dos_campeoes}</Typography>}
                          </Box>
                        ))}
                        {!!an.perguntas?.sem_resposta?.length && (
                          <>
                            <Typography variant="subtitle2">Sem resposta (oportunidade de responder no anúncio)</Typography>
                            <ListaSimples itens={an.perguntas.sem_resposta.map((x) => `${x.pergunta} (${x.mlb})`)} />
                          </>
                        )}
                      </Stack>
                    )}
                    {aba === 5 && (
                      <Stack spacing={1}>
                        {q.grade?.ok && (
                          <Box component="img" src={svc.urlGradeCampeoes(p.id, q.concluido_em)} alt="grade das fotos dos campeões" sx={{ maxWidth: '100%', border: 1, borderColor: 'divider', borderRadius: 1 }} />
                        )}
                        {!q.grade?.ok && <Typography variant="caption" color="text.secondary">Grade não gerada: {q.grade?.motivo || '—'}</Typography>}
                        <Typography variant="body2">
                          <b>Capa:</b> fundo branco em {an.visual?.capa?.fundo_branco ?? '—'} · com texto em {an.visual?.capa?.com_texto ?? '—'} · produto inteiro em {an.visual?.capa?.produto_inteiro ?? '—'}
                        </Typography>
                        <Typography variant="subtitle2">Fotos padrão dos campeões (o plano das 9 copia)</Typography>
                        <ListaSimples itens={an.visual?.fotos_padrao} />
                        <Typography variant="subtitle2">Brecha visual (uma das 9 vai ser esta)</Typography>
                        <ListaSimples itens={an.visual?.brecha} />
                        {an.visual?.observacao && <Typography variant="caption" color="text.secondary">{an.visual.observacao}</Typography>}
                      </Stack>
                    )}
                    {aba === 6 && (
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">Objeções reais e onde o anúncio responde</Typography>
                        {(an.objecoes_para_o_anuncio || []).map((o, i) => (
                          <Typography key={i} variant="body2">
                            • <b>{o.objecao}</b> → {o.responder_em?.join(', ') || '—'}
                          </Typography>
                        ))}
                        {an.catalogo?.observacao && (
                          <Typography variant="body2">
                            <b>Catálogo:</b> {an.catalogo.observacao}
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Box>
                </>
              )}
            </Card>
          )}

          {!!q.avaliacoes?.length && q.status === 'ok' && !an?.resumo && (
            <>
              <Typography variant="subtitle2">Avaliações dos campeões (bruto)</Typography>
              {q.avaliacoes.map((a) => (
                <Box key={a.mlb}>
                  <Typography variant="body2" fontWeight={600}>
                    {a.mlb} · média {a.media ?? '—'} · {a.total ?? 0} avaliações
                  </Typography>
                  {(a.textos || []).filter((t) => t.nota <= 3).slice(0, 3).map((t, i) => (
                    <Typography key={i} variant="caption" display="block" color="text.secondary">
                      ★{t.nota} {t.texto}
                    </Typography>
                  ))}
                  {!(a.textos || []).length && a.por_estrela ? (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {Object.entries(a.por_estrela).reverse().map(([e, n]) => `★${e}: ${n}`).join(' · ')}
                    </Typography>
                  ) : null}
                  {!(a.textos || []).length && a.sem_textos_motivo ? (
                    <Typography variant="caption" display="block" color="warning.main">
                      {a.sem_textos_motivo}
                    </Typography>
                  ) : null}
                </Box>
              ))}
            </>
          )}
        </>
      )}
    </Stack>
  )
}

// ================================================================ etapa 2 — proposta

function EtapaProposta({ p, status, disparar, salvarAprovacao }: { p: Projeto; status: StatusServico | null; disparar: () => Promise<void>; salvarAprovacao: (b: Bloco, a: { status: string; valor?: unknown }) => Promise<void> }) {
  const pr = p.proposta
  const naoUsar = p.pesquisa?.nao_usar || []
  const [tituloEdit, setTituloEdit] = useState(tituloFinal(p))
  const [descEdit, setDescEdit] = useState(descricaoFinal(p))
  useEffect(() => {
    setTituloEdit(tituloFinal(p))
    setDescEdit(descricaoFinal(p))
  }, [p.id, p.proposta?.status, p.aprovacao?.titulo, p.aprovacao?.descricao, p])
  const semChave = status && !status.texto.chave

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button variant="contained" onClick={() => void disparar()} disabled={pr.status === 'rodando' || p.pesquisa?.status !== 'ok' || !!semChave}>
          {pr.status === 'ok' ? 'Gerar proposta de novo' : 'Gerar proposta (Claude)'}
        </Button>
        <StatusChip s={pr.status} />
        {pr.status === 'rodando' && <CircularProgress size={18} />}
        {semChave && <Typography variant="caption" color="error.main">ANTHROPIC_API_KEY não está no .env do serviço — proposta indisponível.</Typography>}
        {p.pesquisa?.status !== 'ok' && <Typography variant="caption" color="text.secondary">Rode a pesquisa antes.</Typography>}
      </Stack>
      {pr.status === 'erro' && <Alert severity="error">{pr.erro}</Alert>}
      {pr.status === 'ok' && (
        <>
          <Alert severity="warning">
            Isto é <b>proposta</b> ({pr.modelo}). A verdade são a pesquisa e o que você confirmou. {pr.a_confirmar?.length ? `Faltam confirmar: ${pr.a_confirmar.join(' · ')}` : 'Nada ficou [A CONFIRMAR].'}
          </Alert>
          <Typography variant="subtitle1" fontWeight={600}>
            Título do anúncio (56–60)
          </Typography>
          <RadioGroup value={tituloEdit} onChange={(e) => setTituloEdit(e.target.value)}>
            {(pr.titulos || []).map((t) => (
              <Box key={t.texto}>
                <FormControlLabel value={t.texto} control={<Radio size="small" />} label={`${t.texto} (${tamanhoTitulo(t.texto)})`} />
                <Checagens itens={checarTitulo(t.texto, naoUsar)} />
              </Box>
            ))}
          </RadioGroup>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField label="Título final (edite à vontade)" value={tituloEdit} onChange={(e) => setTituloEdit(e.target.value)} fullWidth size="small" helperText={`${tamanhoTitulo(tituloEdit)} caracteres`} />
            <Button variant="outlined" onClick={() => void salvarAprovacao('titulo', { status: 'editado', valor: tituloEdit })} disabled={checarTitulo(tituloEdit, naoUsar).some((c) => c.nivel === 'erro')}>
              Aprovar este título
            </Button>
          </Stack>
          <Checagens itens={checarTitulo(tituloEdit, naoUsar)} />
          <Typography variant="subtitle2">Títulos de catálogo (185, reserva)</Typography>
          {(pr.titulos_catalogo || []).map((t) => (
            <Box key={t.texto}>
              <Typography variant="body2">{t.texto}</Typography>
              <Checagens itens={checarTituloCatalogo(t.texto)} />
            </Box>
          ))}
          <Divider />
          <Typography variant="subtitle1" fontWeight={600}>
            Ficha técnica
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Atributo</TableCell>
                <TableCell>Valor proposto</TableCell>
                <TableCell>Fonte</TableCell>
                <TableCell align="right">Campeões preenchem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(pr.ficha || []).map((f) => (
                <TableRow key={f.id} sx={temAConfirmar(f.valor) ? { bgcolor: '#fff8d6' } : undefined}>
                  <TableCell>{f.nome}</TableCell>
                  <TableCell>
                    <TextoMarcado texto={String(f.valor ?? '')} />
                  </TableCell>
                  <TableCell>{f.fonte}</TableCell>
                  <TableCell align="right">{f.campeoes_preenchem ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={() => void salvarAprovacao('ficha_primaria', { status: 'aprovado', valor: pr.ficha })} disabled={(pr.ficha || []).some((f) => temAConfirmar(f.valor))}>
              Aprovar características principais
            </Button>
            <Button variant="outlined" onClick={() => void salvarAprovacao('ficha_secundaria', { status: 'aprovado' })}>
              Aprovar outras características
            </Button>
          </Stack>
          <Divider />
          <Typography variant="subtitle1" fontWeight={600}>
            Descrição (padrão UP)
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField value={descEdit} onChange={(e) => setDescEdit(e.target.value)} multiline minRows={16} fullWidth size="small" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ p: 1.5, maxHeight: 420, overflow: 'auto' }}>
                <TextoMarcado texto={descEdit} />
              </Card>
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="outlined" onClick={() => void salvarAprovacao('descricao', { status: 'editado', valor: descEdit })} disabled={temAConfirmar(descEdit) || !descEdit.trim()}>
              Aprovar descrição
            </Button>
            {temAConfirmar(descEdit) && <Typography variant="caption" color="warning.main">Ainda há [A CONFIRMAR] — resolva no texto antes de aprovar.</Typography>}
          </Stack>
          {!!pr.aprendizados && (
            <>
              <Divider />
              <Typography variant="subtitle2">Bastidores da pesquisa</Typography>
              <Typography variant="body2">
                <b>Objeção nº 1:</b> {pr.aprendizados.objecao_1 || '—'}
              </Typography>
              <Typography variant="body2">
                <b>Decepciona:</b> {(pr.aprendizados.decepciona || []).join(' · ') || '—'}
              </Typography>
              <Typography variant="body2">
                <b>Encanta:</b> {(pr.aprendizados.encanta || []).join(' · ') || '—'}
              </Typography>
              <Typography variant="body2">
                <b>Cada objeção vira:</b> {(pr.aprendizados.cada_objecao_vira || []).join(' · ') || '—'}
              </Typography>
            </>
          )}
        </>
      )}
    </Stack>
  )
}

// ================================================================ etapa 3 — preço

function EtapaPreco({ p, calcular, calcularShopee, salvarAprovacao }: { p: Projeto; calcular: (precos: number[]) => Promise<void>; calcularShopee: (params: { taxa_pct?: number | ''; impostos_pct?: number | ''; mc_alvo_pct: number; markup: number }) => Promise<void>; salvarAprovacao: (b: Bloco | 'preco_shopee', a: { status: string; valor?: unknown }) => Promise<void> }) {
  const base = p.precos?.tabela?.[1]?.preco || (p.pesquisa?.ranking?.itens?.[0]?.preco ?? 0)
  const [precos, setPrecos] = useState<string[]>(() => (p.precos?.tabela?.length ? p.precos.tabela.map((l) => String(l.preco)) : sugerirPrecos(base).map(String)))
  const [ocupado, setOcupado] = useState(false)
  const escolhido = precoFinal(p)
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        MC = preço − comissão (listing_prices) − frete grátis (shipping_options/free, obrigatório acima de R$ 79) − impostos − CMV. Mínimo {MC_MINIMA_PCT}%.
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        {precos.map((v, i) => (
          <TextField key={i} size="small" type="number" label={`Preço ${i + 1}`} value={v} onChange={(e) => setPrecos((xs) => xs.map((x, j) => (j === i ? e.target.value : x)))} sx={{ width: 130 }} />
        ))}
        <Button size="small" onClick={() => setPrecos((xs) => [...xs, ''])} disabled={precos.length >= 6}>
          + preço
        </Button>
        <Button
          variant="contained"
          disabled={ocupado}
          onClick={async () => {
            setOcupado(true)
            try {
              await calcular(precos.map(Number).filter((x) => x > 0))
            } finally {
              setOcupado(false)
            }
          }}
        >
          Calcular MC
        </Button>
        {ocupado && <CircularProgress size={18} />}
      </Stack>
      {!!p.precos?.avisos?.length && <Alert severity="warning">{p.precos.avisos.join(' ')}</Alert>}
      {!!p.precos?.tabela?.length && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell align="right">Preço</TableCell>
              <TableCell align="right">Comissão</TableCell>
              <TableCell align="right">Frete</TableCell>
              <TableCell align="right">Impostos</TableCell>
              <TableCell align="right">CMV</TableCell>
              <TableCell align="right">MC</TableCell>
              <TableCell align="right">MC %</TableCell>
              <TableCell align="right">12x</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {p.precos.tabela.map((l) => (
              <TableRow key={l.preco} sx={l.abaixo_do_minimo ? { bgcolor: '#fdecea' } : escolhido === l.preco ? { bgcolor: '#e8f5e9' } : undefined}>
                <TableCell>
                  <Radio size="small" checked={escolhido === l.preco} disabled={l.abaixo_do_minimo} onChange={() => void salvarAprovacao('preco', { status: 'aprovado', valor: l.preco })} />
                </TableCell>
                <TableCell align="right">{brl(l.preco)}</TableCell>
                <TableCell align="right">
                  {brl((l.comissao || 0) + (l.fixa || 0))} {l.comissao_pct ? `(${l.comissao_pct}%)` : ''}
                  {l.erro_comissao ? ' ⚠' : ''}
                </TableCell>
                <TableCell align="right">
                  {brl(l.frete)}
                  {l.erro_frete ? ' ⚠' : ''}
                </TableCell>
                <TableCell align="right">{brl(l.impostos)}</TableCell>
                <TableCell align="right">{brl(p.precos.cmv)}</TableCell>
                <TableCell align="right">{brl(l.mc)}</TableCell>
                <TableCell align="right">{pct(l.mc_pct)}</TableCell>
                <TableCell align="right">{l.parcela_12x != null ? `12x ${brl(l.parcela_12x)}` : '—'}{l.taxa_fixa_50 ? ' ⚠ taxa fixa 50%' : ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {temCanalShopee(p) && <PrecoShopee p={p} calcular={calcularShopee} salvarAprovacao={salvarAprovacao} />}
      <Typography variant="caption" color="text.secondary">
        Frete só é consultado com a embalagem preenchida em Produto. Linhas em vermelho estão abaixo do mínimo e não podem ser escolhidas. ⚠ = a API não respondeu aquela parcela (o valor ficou 0, confira).
      </Typography>
    </Stack>
  )
}

// ================================================================ etapa 3b — preço Shopee

function PrecoShopee({ p, calcular, salvarAprovacao }: { p: Projeto; calcular: (params: { taxa_pct?: number | ''; impostos_pct?: number | ''; mc_alvo_pct: number; markup: number }) => Promise<void>; salvarAprovacao: (b: 'preco_shopee', a: { status: string; valor?: unknown }) => Promise<void> }) {
  const ps = p.precos_shopee || {}
  const [mc, setMc] = useState(String(ps.mc_alvo_pct ?? SHOPEE_MC_ALVO_PCT))
  const [markup, setMarkup] = useState(String(ps.markup ?? SHOPEE_MARKUP_CHEIO))
  const [taxa, setTaxa] = useState<string>('')
  const [imp, setImp] = useState<string>('')
  const [ocupado, setOcupado] = useState(false)
  const cmv = Number(p.produto.cmv || p.pesquisa?.cmv?.cmv || 0)
  const simul = precificarShopee({ cmv, taxaPct: Number(taxa || ps.taxa_pct || 0), impostosPct: Number(imp || ps.impostos_pct || 0), mcAlvoPct: Number(mc), markup: Number(markup) })
  const aprovado = precoShopeeAprovado(p)
  return (
    <Card variant="outlined" sx={{ p: 1.5, bgcolor: '#fff7f2' }}>
      <Typography variant="subtitle1" fontWeight={600}>
        Shopee — {LOJAS_SHOPEE[(p.produto.loja_shopee || '1') as LojaShopee]}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Regra da casa: o <b>preço-alvo</b> é o que dá a MC alvo depois da taxa e dos impostos; o <b>preço cheio</b> publicado é o alvo × {markup}, e o desconto de loja leva de volta ao alvo. Taxa e impostos vêm dos pedidos Shopee da loja em <code>margens</code> (30 dias); sem base, o serviço usa o parâmetro e avisa.
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField size="small" type="number" label="MC alvo (%)" value={mc} onChange={(e) => setMc(e.target.value)} sx={{ width: 120 }} />
        <TextField size="small" type="number" label="Markup do cheio" value={markup} onChange={(e) => setMarkup(e.target.value)} sx={{ width: 140 }} inputProps={{ step: 0.05 }} />
        <TextField size="small" type="number" label="Taxa (%) — vazio = medir" value={taxa} onChange={(e) => setTaxa(e.target.value)} sx={{ width: 190 }} />
        <TextField size="small" type="number" label="Impostos (%) — vazio = medir" value={imp} onChange={(e) => setImp(e.target.value)} sx={{ width: 210 }} />
        <Button
          variant="contained"
          disabled={ocupado || cmv <= 0}
          onClick={async () => {
            setOcupado(true)
            try {
              await calcular({ taxa_pct: taxa === '' ? '' : Number(taxa), impostos_pct: imp === '' ? '' : Number(imp), mc_alvo_pct: Number(mc), markup: Number(markup) })
            } finally {
              setOcupado(false)
            }
          }}
        >
          Calcular Shopee
        </Button>
        {ocupado && <CircularProgress size={18} />}
      </Stack>
      {cmv <= 0 && <Alert severity="warning" sx={{ mt: 1 }}>Sem CMV não há preço-alvo (regra do CMV zero). Informe em Produto.</Alert>}
      {ps.aviso && <Alert severity="info" sx={{ mt: 1 }}>{ps.aviso}</Alert>}
      {ps.erro && <Alert severity="error" sx={{ mt: 1 }}>{ps.erro}</Alert>}
      {ps.ok && (
        <Table size="small" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell align="right">Preço</TableCell>
              <TableCell align="right">Taxa Shopee</TableCell>
              <TableCell align="right">Impostos</TableCell>
              <TableCell align="right">CMV</TableCell>
              <TableCell align="right">MC</TableCell>
              <TableCell align="right">MC %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ bgcolor: '#e8f5e9' }}>
              <TableCell>Preço-alvo (com desconto)</TableCell>
              <TableCell align="right">{brl(ps.preco_alvo)}</TableCell>
              <TableCell align="right">{pct(ps.taxa_pct)}</TableCell>
              <TableCell align="right">{pct(ps.impostos_pct)}</TableCell>
              <TableCell align="right">{brl(ps.cmv)}</TableCell>
              <TableCell align="right">{brl(ps.mc_no_alvo)}</TableCell>
              <TableCell align="right">{pct(ps.mc_no_alvo_pct)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Preço cheio publicado (desconto de {pct(ps.desconto_pct)})</TableCell>
              <TableCell align="right">{brl(ps.preco_cheio)}</TableCell>
              <TableCell align="right">{pct(ps.taxa_pct)}</TableCell>
              <TableCell align="right">{pct(ps.impostos_pct)}</TableCell>
              <TableCell align="right">{brl(ps.cmv)}</TableCell>
              <TableCell align="right">{brl(ps.mc_no_cheio)}</TableCell>
              <TableCell align="right">{pct(ps.mc_no_cheio_pct)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
        <Button variant={aprovado ? 'contained' : 'outlined'} color="success" disabled={!ps.ok} onClick={() => void salvarAprovacao('preco_shopee', { status: 'aprovado', valor: { preco_cheio: ps.preco_cheio, preco_alvo: ps.preco_alvo } })}>
          {aprovado ? 'Preço Shopee aprovado' : 'Aprovar preço Shopee'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Taxa: {ps.taxa_fonte || '—'}. {simul.ok && !ps.ok ? `Simulação local: alvo ${brl(simul.precoAlvo)} · cheio ${brl(simul.precoCheio)}.` : ''}
        </Typography>
      </Stack>
    </Card>
  )
}

// ================================================================ etapa 4 — fotos

function EtapaFotos({ p, status, gerar, salvarFoto, salvarPlano }: { p: Projeto; status: StatusServico | null; gerar: (ns?: number[]) => Promise<void>; salvarFoto: (n: number, a: { aprovacao: string; ajuste?: string }) => Promise<void>; salvarPlano: (plano: Array<{ n: number; tema: string }>) => Promise<void> }) {
  const [sel, setSel] = useState<number[]>([])
  const [ajuste, setAjuste] = useState<Record<number, string>>({})
  const [plano, setPlano] = useState(p.fotos.plano)
  useEffect(() => setPlano(p.fotos.plano), [p.fotos.plano])
  const rodando = p.fotos.status === 'rodando'
  const pronto = status?.imagem.pronto
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button variant="contained" disabled={rodando || !pronto || !p.fotos_reais.length} onClick={() => void gerar()}>
          Gerar as 9 fotos
        </Button>
        <Button variant="outlined" disabled={rodando || !pronto || !sel.length} onClick={() => void gerar(sel)}>
          Gerar só as marcadas ({sel.length})
        </Button>
        <Button size="small" onClick={() => void salvarPlano(plano.map((f) => ({ n: f.n, tema: f.tema })))}>
          Salvar temas editados
        </Button>
        <Button size="small" color="success" disabled={rodando || !Object.values(p.fotos.itens || {}).some((g) => g.status === 'ok' && g.aprovacao !== 'aprovada')}
          onClick={() => { for (const [n, g] of Object.entries(p.fotos.itens || {})) if (g.status === 'ok' && g.aprovacao !== 'aprovada') void salvarFoto(Number(n), { aprovacao: 'aprovada' }) }}>
          Aprovar todas as geradas
        </Button>
        <StatusChip s={p.fotos.status} />
        {rodando && <CircularProgress size={18} />}
        <Typography variant="caption" color="text.secondary">
          Provedor: {status?.imagem.provedor} ({status?.imagem.provedor === 'openai' ? status?.imagem.openai.modelo : status?.imagem.gemini.modelo}) {pronto ? '' : '— sem chave no .env do serviço'}.
          {!p.fotos_reais.length ? ' Envie fotos reais em Produto.' : ''}
        </Typography>
      </Stack>
      {p.fotos.erro && <Alert severity="error">{p.fotos.erro}</Alert>}
      <Alert severity="info">
        Cada foto passa por uma auditoria automática (o Claude compara a gerada com a foto real e regenera até 2 vezes se reprovar); o resultado aparece no selo. A decisão final é sua, foto a foto: reprove com "Pedir ajuste" e gere de novo só aquela — o texto do ajuste entra no prompt. Capa: fundo branco forçado, sem texto, ≥ 1200 px.
      </Alert>
      <Grid container spacing={1.5}>
        {plano.map((f) => {
          const g = p.fotos.itens?.[String(f.n)]
          return (
            <Grid key={f.n} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                {g?.status === 'ok' ? (
                  <Box component="a" href={svc.urlFotoGerada(p.id, f.n)} target="_blank" rel="noreferrer">
                    <Box component="img" src={svc.urlFotoGerada(p.id, f.n, p.atualizado_em)} alt={`Foto ${f.n}`} sx={{ width: '100%', aspectRatio: '1', objectFit: 'contain', bgcolor: '#fafafa' }} />
                  </Box>
                ) : (
                  <Box sx={{ aspectRatio: '1', display: 'grid', placeItems: 'center', bgcolor: '#f5f5f5', color: 'text.secondary' }}>
                    {g?.status === 'gerando' ? <CircularProgress /> : g?.status === 'erro' ? 'erro' : 'não gerada'}
                  </Box>
                )}
                <CardContent sx={{ pt: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Checkbox size="small" checked={sel.includes(f.n)} onChange={(e) => setSel((s) => (e.target.checked ? [...s, f.n] : s.filter((x) => x !== f.n)))} />
                    <Typography variant="subtitle2">
                      {f.n}. {f.tipo}
                    </Typography>
                    {g?.aprovacao === 'aprovada' && <Chip size="small" color="success" label="aprovada" />}
                    {g?.aprovacao === 'ajuste' && <Chip size="small" color="warning" label="ajuste pedido" />}
                    {g?.ampliada && (
                      <Tooltip title={`O modelo devolveu ${g.largura}×${g.altura} e o serviço ampliou para 1200 px.`}>
                        <Chip size="small" label="ampliada" />
                      </Tooltip>
                    )}
                  </Stack>
                  <TextField size="small" fullWidth multiline value={f.tema} onChange={(e) => setPlano((pl) => pl.map((x) => (x.n === f.n ? { ...x, tema: e.target.value } : x)))} sx={{ mt: 0.5 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Responde: {f.responde || f.objecao || '—'}
                  </Typography>
                  {g?.auditoria?.feita && (
                    <Chip size="small" sx={{ mt: 0.5 }} color={g.auditoria.aprovada ? 'success' : 'error'} variant="outlined" label={g.auditoria.aprovada ? 'auditoria IA: ok' : 'auditoria IA: reprovou'} title={`${g.auditoria.motivo || ''}${g.auditoria.tentativas?.length ? ` · ${g.auditoria.tentativas.length} tentativa(s)` : ''}`} />
                  )}
                  {g?.status === 'erro' && (
                    <Typography variant="caption" color="error.main" display="block">
                      {g.erro}
                    </Typography>
                  )}
                  {g?.status === 'ok' && (
                    <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                      <Typography variant="caption" color="text.secondary">
                        {g.largura}×{g.altura} · {g.modelo}
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        <Button size="small" variant={g.aprovacao === 'aprovada' ? 'contained' : 'outlined'} color="success" onClick={() => void salvarFoto(f.n, { aprovacao: 'aprovada' })}>
                          Aprovar
                        </Button>
                        <Button size="small" variant="outlined" color="warning" onClick={() => void salvarFoto(f.n, { aprovacao: 'ajuste', ajuste: ajuste[f.n] || '' })}>
                          Pedir ajuste
                        </Button>
                      </Stack>
                      <TextField size="small" placeholder="o que ajustar (vai para o prompt na próxima geração)" value={ajuste[f.n] ?? g.ajuste ?? ''} onChange={(e) => setAjuste((a) => ({ ...a, [f.n]: e.target.value }))} />
                    </Stack>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )
        })}
      </Grid>
    </Stack>
  )
}

// ================================================================ etapa 5 — aprovação (pré-visualização estilo ML)

function EtapaAprovacao({ p, salvarAprovacao }: { p: Projeto; salvarAprovacao: (b: Bloco, a: { status: string; valor?: unknown; nota?: string }) => Promise<void> }) {
  const [notas, setNotas] = useState<Record<string, string>>({})
  const n = blocosAprovados(p)
  const fotos = fotosAprovadas(p)
  const [foto, setFoto] = useState(fotos[0] || 1)
  return (
    <Stack spacing={2}>
      <Box sx={{ bgcolor: '#fff159', p: 1, borderRadius: 1 }}>
        <Typography variant="subtitle2">
          Pré-visualização do anúncio — {n} de {BLOCOS.length} aprovados
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack direction="row" spacing={1}>
            <Stack spacing={0.5}>
              {fotos.map((k) => (
                <Box key={k} component="img" src={svc.urlFotoGerada(p.id, k, p.atualizado_em)} onClick={() => setFoto(k)} sx={{ width: 48, height: 48, objectFit: 'contain', border: '1px solid', borderColor: foto === k ? 'primary.main' : 'divider', borderRadius: 1, cursor: 'pointer' }} />
              ))}
            </Stack>
            <Box sx={{ flex: 1, minHeight: 320, bgcolor: '#fafafa', display: 'grid', placeItems: 'center' }}>
              {fotos.length ? <Box component="img" src={svc.urlFotoGerada(p.id, foto, p.atualizado_em)} sx={{ maxWidth: '100%', maxHeight: 420, objectFit: 'contain' }} /> : <Typography color="text.secondary">nenhuma foto aprovada</Typography>}
            </Box>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Typography variant="caption" color="text.secondary">
            Novo · {p.produto.marca}
          </Typography>
          <Typography variant="h6">{tituloFinal(p) || '(sem título)'}</Typography>
          <Typography variant="h4" sx={{ mt: 1 }}>
            {brl(precoFinal(p))}
          </Typography>
          <Typography variant="caption" color="success.main">
            {(precoFinal(p) || 0) >= 79 ? 'Frete grátis' : 'Frete a calcular'}
          </Typography>
          {temCanalShopee(p) && p.precos_shopee?.ok && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Shopee: de <s>{brl(p.precos_shopee.preco_cheio)}</s> por <b>{brl(p.precos_shopee.preco_alvo)}</b> ({pct(p.precos_shopee.desconto_pct)} off) {precoShopeeAprovado(p) ? '· aprovado' : '· pendente'}
            </Typography>
          )}
          <Stack spacing={1} sx={{ mt: 2 }}>
            <Button variant="contained" disabled fullWidth>
              Comprar agora
            </Button>
            <Button variant="outlined" disabled fullWidth>
              Adicionar ao carrinho
            </Button>
          </Stack>
        </Grid>
      </Grid>
      <Typography variant="subtitle1" fontWeight={600}>
        Blocos
      </Typography>
      <Table size="small">
        <TableBody>
          {BLOCOS.map((b) => {
            const a = p.aprovacao?.[b]
            return (
              <TableRow key={b}>
                <TableCell sx={{ width: 240 }}>{BLOCO_LABEL[b]}</TableCell>
                <TableCell>
                  <StatusChip s={a?.status} />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Button size="small" variant="outlined" color="success" onClick={() => void salvarAprovacao(b, { status: a?.status === 'editado' ? 'editado' : 'aprovado', valor: a?.valor })} disabled={b === 'imagens' && !fotos.length}>
                      Aprovar
                    </Button>
                    <Button size="small" onClick={() => void salvarAprovacao(b, { status: 'pendente', valor: a?.valor })}>
                      Voltar a pendente
                    </Button>
                  </Stack>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {b === 'imagens' ? `${fotos.length} fotos aprovadas` : b === 'titulo' ? tituloFinal(p) : b === 'preco' ? brl(precoFinal(p)) : b === 'descricao' ? `${descricaoFinal(p).length} caracteres` : ''}
                  </Typography>
                  {(b === 'ficha_primaria' || b === 'ficha_secundaria') && (
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
                      <TextField size="small" placeholder="comentário (o que corrigir na ficha)" value={notas[b] ?? a?.nota ?? ''} onChange={(e) => setNotas((n) => ({ ...n, [b]: e.target.value }))} sx={{ minWidth: 260 }} inputProps={{ style: { fontSize: 12, padding: '4px 6px' } }} />
                      <Button size="small" onClick={() => void salvarAprovacao(b, { status: a?.status || 'pendente', valor: a?.valor, nota: notas[b] ?? a?.nota ?? '' })}>Comentar</Button>
                    </Stack>
                  )}
                  {a?.nota && !(b === 'ficha_primaria' || b === 'ficha_secundaria') ? <Typography variant="caption" display="block">{a.nota}</Typography> : null}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <Typography variant="subtitle2">Descrição</Typography>
      <Card variant="outlined" sx={{ p: 1.5, maxHeight: 360, overflow: 'auto' }}>
        <TextoMarcado texto={descricaoFinal(p)} />
      </Card>
    </Stack>
  )
}

// ================================================================ etapa 6 — publicar

function EtapaPublicar({ p, publicar, publicarShopee }: { p: Projeto; publicar: () => Promise<void>; publicarShopee: () => Promise<void> }) {
  const pend = pendenciasPublicacao(p)
  const pendS = pendenciasShopee(p)
  const [dlg, setDlg] = useState<false | 'ml' | 'shopee'>(false)
  const [conf, setConf] = useState('')
  const [previaS, setPreviaS] = useState<{ pendencias: string[]; corpo: Record<string, unknown> } | null>(null)
  const pubS = p.publicacao_shopee || { status: 'nenhuma' }
  const [previa, setPrevia] = useState<{ pendencias: string[]; corpo: Record<string, unknown> } | null>(null)
  const [ocupado, setOcupado] = useState(false)
  const pub = p.publicacao
  return (
    <Stack spacing={2}>
      <Alert severity={podePublicar(p) ? 'success' : 'warning'}>
        {podePublicar(p) ? 'Checklist final OK. O anúncio será criado PAUSADO na conta ' + p.produto.conta + '.' : 'Ainda falta:'}
        {!!pend.length && (
          <ul style={{ margin: '4px 0 0 16px' }}>
            {pend.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
        )}
      </Alert>
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          onClick={async () => {
            setOcupado(true)
            try {
              const r = await svc.previaPublicacao(p.id)
              setPrevia({ pendencias: r.pendencias, corpo: r.corpo })
            } finally {
              setOcupado(false)
            }
          }}
        >
          Ver o que será enviado (sem gravar)
        </Button>
        <Button variant="contained" color="error" disabled={!podePublicar(p) || ocupado || pub.status === 'pausado'} onClick={() => setDlg('ml')}>
          Publicar PAUSADO no Mercado Livre
        </Button>
      </Stack>
      {temCanalShopee(p) && (
        <>
          <Divider />
          <Alert severity={podePublicarShopee(p) ? 'success' : 'warning'}>
            {podePublicarShopee(p) ? `Shopee OK. O item será criado NÃO LISTADO (UNLIST) na ${LOJAS_SHOPEE[(p.produto.loja_shopee || '1') as LojaShopee]}, com preço cheio ${brl(p.precos_shopee?.preco_cheio)} e desconto de loja até ${brl(p.precos_shopee?.preco_alvo)}.` : 'Shopee — ainda falta:'}
            {!!pendS.length && (
              <ul style={{ margin: '4px 0 0 16px' }}>
                {pendS.map((x) => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            )}
          </Alert>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              onClick={async () => {
                setOcupado(true)
                try {
                  const r = await svc.previaShopee(p.id)
                  setPreviaS({ pendencias: r.pendencias, corpo: r.corpo })
                } finally {
                  setOcupado(false)
                }
              }}
            >
              Ver o que vai para a Shopee (sem gravar)
            </Button>
            <Button variant="contained" color="error" disabled={!podePublicarShopee(p) || ocupado || pubS.status === 'unlist'} onClick={() => setDlg('shopee')}>
              Enviar para a Shopee (não listado)
            </Button>
          </Stack>
          {previaS && (
            <Card variant="outlined" sx={{ p: 1.5 }}>
              {!!previaS.pendencias.length && <Alert severity="warning">Serviço: {previaS.pendencias.join(' · ')}</Alert>}
              <Typography component="pre" variant="caption" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
                {JSON.stringify(previaS.corpo, null, 1)}
              </Typography>
            </Card>
          )}
          {pubS.status === 'unlist' && (
            <Alert severity="success">
              Item criado na Shopee, não listado: {pubS.item_id}{' '}
              {pubS.link && (
                <Link href={pubS.link} target="_blank" rel="noreferrer">
                  abrir
                </Link>
              )}
              . {pubS.desconto && (pubS.desconto as { discount_id?: number }).discount_id ? 'Desconto de loja criado (começa em 1 h).' : 'Desconto não criado — faça pelo Seller Centre.'} Liste pelo Seller Centre quando quiser.
            </Alert>
          )}
          {pubS.status === 'erro' && <Alert severity="error">Shopee: {pubS.erro}</Alert>}
        </>
      )}
      {previa && (
        <Card variant="outlined" sx={{ p: 1.5 }}>
          {!!previa.pendencias.length && <Alert severity="warning">Serviço: {previa.pendencias.join(' · ')}</Alert>}
          <Typography component="pre" variant="caption" sx={{ whiteSpace: 'pre-wrap', m: 0 }}>
            {JSON.stringify(previa.corpo, null, 1)}
          </Typography>
        </Card>
      )}
      {pub.status === 'pausado' && (
        <Alert severity="success">
          Anúncio criado PAUSADO: {pub.item_id}{' '}
          {pub.permalink && (
            <Link href={pub.permalink} target="_blank" rel="noreferrer">
              abrir no ML
            </Link>
          )}
          . Ative pelo painel do Mercado Livre quando quiser.
        </Alert>
      )}
      {(pub.status === 'erro' || pub.status === 'validacao_falhou') && (
        <Alert severity="error">
          {pub.status}: {pub.erro || JSON.stringify((pub as unknown as { validacao?: unknown }).validacao)}
        </Alert>
      )}
      <Dialog open={!!dlg} onClose={() => setDlg(false)}>
        <DialogTitle>{dlg === 'shopee' ? 'Gravar na Shopee?' : 'Gravar no Mercado Livre?'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {dlg === 'shopee'
              ? `Vai subir as fotos, criar o item NÃO LISTADO na ${LOJAS_SHOPEE[(p.produto.loja_shopee || '1') as LojaShopee]} e o desconto de loja até o preço-alvo. Digite SHOPEE para confirmar.`
              : `Vai rodar POST /items/validate → POST /items → PUT paused → descrição → catálogo na conta ${p.produto.conta}. O anúncio nasce pausado. Digite PAUSADO para confirmar.`}
          </Typography>
          <TextField autoFocus fullWidth size="small" value={conf} onChange={(e) => setConf(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            disabled={conf !== (dlg === 'shopee' ? 'SHOPEE' : 'PAUSADO') || ocupado}
            onClick={async () => {
              setOcupado(true)
              try {
                if (dlg === 'shopee') await publicarShopee()
                else await publicar()
                setDlg(false)
                setConf('')
              } finally {
                setOcupado(false)
              }
            }}
          >
            {dlg === 'shopee' ? 'Enviar não listado' : 'Publicar pausado'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

// ================================================================ página

export function FabricaAnunciosPage() {
  const [status, setStatus] = useState<StatusServico | null>(null)
  const [lista, setLista] = useState<svc.ResumoProjeto[]>([])
  const [p, setP] = useState<Projeto | null>(null)
  const [emAndamento, setEmAndamento] = useState<string[]>([])
  const [etapa, setEtapa] = useState(0)
  const [erro, setErro] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)

  const carregarLista = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([svc.fetchStatus(), svc.fetchProjetos()])
      setStatus(s)
      setLista(l.projetos)
      setErro(null)
    } catch (e) {
      setErro(`O serviço da Fábrica não respondeu (${msg(e)}). Ele roda em services/fabrica-anuncios, porta 8104.`)
    } finally {
      setCarregando(false)
    }
  }, [])
  useEffect(() => {
    void carregarLista()
  }, [carregarLista])

  const abrir = useCallback(async (id: string, irParaEtapa = true) => {
    try {
      const r = await svc.fetchProjeto(id)
      setP(r.projeto)
      setEmAndamento(r.em_andamento)
      if (irParaEtapa) setEtapa(etapaSugerida(r.projeto))
    } catch (e) {
      setErro(msg(e))
    }
  }, [])

  // Polling enquanto há pesquisa / proposta / fotos rodando no serviço.
  const rodando = !!p && (emAndamento.length > 0 || p.pesquisa.status === 'rodando' || p.proposta.status === 'rodando' || p.fotos.status === 'rodando')
  useEffect(() => {
    if (!rodando || !p) return
    const t = window.setInterval(() => void abrir(p.id, false), 4000)
    return () => window.clearInterval(t)
  }, [rodando, p, abrir])

  const salvo = (novo: Projeto) => {
    setP(novo)
    void carregarLista()
  }
  const acao = async (fn: () => Promise<unknown>) => {
    try {
      await fn()
      if (p) await abrir(p.id, false)
      void carregarLista()
    } catch (e) {
      setErro(msg(e))
    }
  }
  const salvarAprovacao = (b: Bloco | 'preco_shopee', a: { status: string; valor?: unknown; nota?: string }) => acao(() => svc.editarProjeto(p!.id, { aprovacao: { [b]: a } }))

  const stats = useMemo(
    () =>
      status
        ? [
            { label: 'Texto (Claude)', value: status.texto.chave ? 'pronto' : 'sem chave', hint: status.texto.modelo },
            { label: `Fotos (${status.imagem.provedor})`, value: status.imagem.pronto ? 'pronto' : 'sem chave', hint: status.imagem.provedor === 'openai' ? status.imagem.openai.modelo : status.imagem.gemini.modelo },
            { label: 'Token ML (Mongo)', value: status.ml.mongo ? 'ok' : 'falta MONGO_URI' },
            { label: 'Projetos', value: String(lista.length) },
          ]
        : undefined,
    [status, lista.length],
  )

  return (
    <DomainPageShell title="Fábrica de Anúncios" subtitle="Anúncio novo no Mercado Livre pelo método UP: pesquisa → proposta → preço → fotos → aprovação → PAUSADO. Nada vai para o ML sem o seu OK." loading={carregando} error={erro} onRetry={carregarLista} stats={stats}>
      <Stack spacing={2}>
        <PainelReferencias marcaAtual={p?.produto.marca || 'FIESTA PARTY FESTA'} conta={p?.produto.conta || 'FIESTAPARTYFESTA'} />
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card variant="outlined">
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" fontWeight={600}>
                    Projetos
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      setP(null)
                      setEtapa(0)
                    }}
                  >
                    + Novo
                  </Button>
                </Stack>
                <List dense>
                  {lista.map((r) => (
                    <ListItemButton key={r.id} selected={p?.id === r.id} onClick={() => void abrir(r.id)}>
                      <ListItemText primary={r.produto} secondary={`${r.marca} · ${(r.canais || ['ml']).map((c) => (c === 'ml' ? 'ML' : 'Shopee')).join('+')} · ${r.aprovados}/6 · ${r.publicacao === 'pausado' ? 'PAUSADO no ML' : r.fotos_ok + ' fotos'}${r.publicacao_shopee === 'unlist' ? ' · Shopee não listado' : ''} · ${fmtData(r.atualizado_em)}`} />
                    </ListItemButton>
                  ))}
                  {!lista.length && (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                      Nenhum projeto ainda.
                    </Typography>
                  )}
                </List>
                {p && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      if (window.confirm('Apagar este projeto da Fábrica? (não mexe no Mercado Livre)')) void acao(() => svc.apagarProjeto(p.id)).then(() => setP(null))
                    }}
                  >
                    apagar projeto
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 9 }}>
            <Card variant="outlined">
              <CardContent>
                <Stepper nonLinear activeStep={etapa} alternativeLabel sx={{ mb: 2 }}>
                  {ETAPAS.map((label, i) => (
                    <Step key={label} completed={p ? i < etapaSugerida(p) : false}>
                      <StepButton onClick={() => setEtapa(i)} disabled={!p && i > 0}>
                        {label}
                      </StepButton>
                    </Step>
                  ))}
                </Stepper>
                {p && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {p.produto.nome} · {p.produto.conta} · atualizado {fmtData(p.atualizado_em)} {rodando ? '· processando…' : ''}
                  </Typography>
                )}
                {etapa === 0 && <EtapaProduto p={p} onSalvo={salvo} />}
                {p && etapa === 1 && (
                  <EtapaPesquisa
                    p={p}
                    status={status}
                    disparar={(so) => acao(() => svc.dispararPesquisa(p.id, so))}
                    salvarLinks={(texto, modo) => acao(() => svc.salvarConcorrentes(p.id, texto, modo))}
                  />
                )}
                {p && etapa === 2 && <EtapaProposta p={p} status={status} disparar={() => acao(() => svc.dispararProposta(p.id))} salvarAprovacao={salvarAprovacao} />}
                {p && etapa === 3 && <EtapaPreco p={p} calcular={(precos) => acao(() => svc.calcularPrecos(p.id, precos))} calcularShopee={(params) => acao(() => svc.calcularPrecosShopee(p.id, params))} salvarAprovacao={salvarAprovacao} />}
                {p && etapa === 4 && (
                  <EtapaFotos
                    p={p}
                    status={status}
                    gerar={(ns) => acao(() => svc.dispararFotos(p.id, ns))}
                    salvarFoto={(n, a) => acao(() => svc.editarProjeto(p.id, { fotos: { [n]: a } }))}
                    salvarPlano={(plano) => acao(() => svc.editarProjeto(p.id, { plano }))}
                  />
                )}
                {p && etapa === 5 && <EtapaAprovacao p={p} salvarAprovacao={salvarAprovacao} />}
                {p && etapa === 6 && <EtapaPublicar p={p} publicar={() => acao(() => svc.publicarPausado(p.id))} publicarShopee={() => acao(() => svc.publicarShopee(p.id))} />}
                {/* Navegação entre etapas (pedido da Mariana, 24/09/2026): avançar sem precisar clicar no topo. */}
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center" sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Button disabled={etapa === 0} onClick={() => setEtapa((e) => e - 1)}>
                    ← Voltar
                  </Button>
                  <Typography variant="caption" color="text.secondary">
                    Etapa {etapa + 1} de {ETAPAS.length} · {ETAPAS[etapa]}
                    {p && etapa < etapaSugerida(p) ? ' · concluída' : ''}
                  </Typography>
                  {etapa < ETAPAS.length - 1 ? (
                    <Button variant="contained" disabled={!p} title={p ? '' : 'Crie o projeto para avançar'} onClick={() => setEtapa((e) => e + 1)}>
                      Avançar para {ETAPAS[etapa + 1]} →
                    </Button>
                  ) : (
                    <span />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Stack>
    </DomainPageShell>
  )
}
